<template>
  <b-modal
    :id="modalId"
    :title="modalTitle"
    hide-footer
    size="lg"
    centered
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <div
      v-if="loading"
      class="d-flex justify-content-center align-items-center"
      style="height: 400px;"
    >
      <b-spinner
        variant="primary"
        label="Loading map..."
      />
    </div>

    <div
      v-else-if="error"
      class="text-center p-4"
    >
      <div class="alert alert-danger">
        <h5>Map Loading Error</h5>
        <p>{{ error }}</p>
        <div class="d-flex justify-content-center gap-2 flex-wrap">
          <b-button
            variant="primary"
            @click="retryMapInitialization"
          >
            Retry
          </b-button>
          <b-button
            v-if="map"
            variant="outline-primary"
            @click="forceMapRender"
          >
            Force Render
          </b-button>
          <b-button
            v-if="locationData && (locationData.latitude || locationData.address)"
            variant="outline-info"
            @click="openInGoogleMaps"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              class="mr-1"
            />
            Open in Google Maps
          </b-button>
        </div>
      </div>
    </div>

    <div v-else>
      <!-- Location Info -->
      <div
        v-if="locationData"
        class="mb-3 p-3 bg-light rounded"
      >
        <h6 class="mb-2">
          <feather-icon
            icon="MapPinIcon"
            class="mr-1"
          />
          {{ locationData.address || 'Location Address' }}
        </h6>
        <div
          v-if="locationData.latitude && locationData.longitude"
          class="text-muted small"
        >
          Coordinates: {{ locationData.latitude }}, {{ locationData.longitude }}
        </div>
        <div
          v-if="locationData.source"
          class="text-muted small"
        >
          Source: {{ locationData.source }}
        </div>
      </div>

      <!-- Map Container -->
      <div
        ref="mapContainer"
        class="map-container"
        style="width: 100%; height: 400px; border-radius: 6px; overflow: hidden; border: 1px solid #dee2e6;"
      />

      <!-- Action Buttons -->
      <div class="mt-3 d-flex justify-content-between">
        <div>
          <b-button
            v-if="locationData && (locationData.latitude || locationData.address)"
            variant="outline-primary"
            size="sm"
            @click="openInGoogleMaps"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              class="mr-1"
            />
            Open in Google Maps
          </b-button>
        </div>
        <div>
          <b-button
            variant="secondary"
            @click="closeModal"
          >
            Close
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BSpinner,
  BButton,
} from 'bootstrap-vue'
import ErrorMessage from '@core/mixins/ErrorMessage'

export default {
  name: 'BaseLocationViewModal',
  components: {
    BModal,
    BSpinner,
    BButton,
  },
  mixins: [ErrorMessage],
  props: {
    modalId: {
      type: String,
      required: true,
    },
    modalTitle: {
      type: String,
      default: 'View Location',
    },
    locationData: {
      type: Object,
      default: null,
    },
    googleApiKey: {
      type: String,
      default: process.env.VUE_APP_GOOGLE_MAPS_API_KEY || '',
    },
  },
  data() {
    return {
      map: null,
      marker: null,
      geocoder: null,
      loading: false,
      error: null,
      retryCount: 0,
      maxRetries: 5,
    }
  },
  beforeDestroy() {
    this.cleanupMap()
  },
  methods: {
    openModal() {
      this.retryCount = 0
      this.$bvModal.show(this.modalId)
    },
    closeModal() {
      this.$bvModal.hide(this.modalId)
    },
    onModalShown() {
      this.$nextTick(() => {
        // Add a small delay to ensure modal is fully rendered
        setTimeout(() => {
          this.initializeMap()
        }, 100)
      })
    },
    onModalHidden() {
      this.cleanupMap()
    },
    async initializeMap() {
      this.loading = true
      this.error = null

      try {
        // Use the same approach as BaseLocationPicker
        if (window.google && window.google.maps && window.google.maps.places) {
          this.ensureMap()
        } else {
          await this.loadGoogleMapsScript()
          await this.waitForGoogleMaps()
          this.ensureMap()
        }

        this.loading = false
      } catch (error) {
        this.loading = false

        // Provide more specific error messages
        if (error.message.includes('API key')) {
          this.error = 'Google Maps API key is missing or invalid. Please contact support.'
        } else if (error.message.includes('timeout')) {
          this.error = 'Google Maps failed to load within the timeout period. Please check your internet connection and try again.'
        } else if (error.message.includes('blocked by ad blocker') || error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
          this.error = 'Google Maps is blocked by your ad blocker or browser security settings. Please disable ad blockers for this site or try opening in incognito/private mode.'
        } else if (error.message.includes('script')) {
          this.error = 'Failed to load Google Maps script. This might be due to network issues or ad blockers. Please try again.'
        } else {
          this.error = 'Failed to load Google Maps. Please check your internet connection and try again.'
        }
      }
    },
    loadGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        // Check if Google Maps is already loaded
        if (window.google && window.google.maps) {
          resolve()
          return
        }

        // Check if our specific script is already loaded
        if (document.getElementById('gmaps-view-modal-script')) {
          // Wait for the existing script to load
          this.waitForGoogleMaps().then(resolve).catch(reject)
          return
        }

        if (!this.googleApiKey) {
          reject(new Error('Google Maps API key not found'))
          return
        }
        const script = document.createElement('script')
        script.id = 'gmaps-view-modal-script'
        script.type = 'text/javascript'
        script.async = true
        script.defer = true

        // Use the same approach as BaseLocationPicker (which works)
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleApiKey}&libraries=places&loading=async`

        // Add timeout to prevent hanging
        const timeout = setTimeout(() => {
          reject(new Error('Google Maps script load timeout'))
        }, 10000) // 10 second timeout

        script.onload = () => {
          clearTimeout(timeout)
          // Wait a bit more for Google Maps to be fully initialized
          setTimeout(() => {
            if (window.google && window.google.maps) {
              resolve()
            } else {
              reject(new Error('Google Maps not initialized after script load'))
            }
          }, 100)
        }

        script.onerror = error => {
          clearTimeout(timeout)
          // Check if it's a client-side blocking issue
          if (error && error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            reject(new Error('Google Maps blocked by ad blocker or browser security. Please disable ad blockers for this site or try in incognito mode.'))
          } else {
            reject(new Error('Failed to load Google Maps script. Please check your internet connection and try again.'))
          }
        }

        document.head.appendChild(script)
      })
    },
    waitForGoogleMaps() {
      return new Promise((resolve, reject) => {
        let attempts = 0
        const maxAttempts = 100 // 5 seconds max wait time

        const check = () => {
          attempts += 1

          if (window.google && window.google.maps && window.google.maps.places) {
            resolve()
          } else if (attempts >= maxAttempts) {
            reject(new Error('Google Maps not available after timeout'))
          } else {
            setTimeout(check, 50)
          }
        }
        check()
      })
    },
    ensureMap() {
      if (!window.google || !window.google.maps) {
        this.error = 'Google Maps API not loaded. Please refresh the page and try again.'
        this.loading = false
        return
      }

      // Wait for container to be available with retry mechanism
      this.waitForContainer().then(() => {
        if (this.map) {
          window.google.maps.event.trigger(this.map, 'resize')
          return
        }
        this.createMap()
      }).catch(() => {
        this.error = 'Map container not found. Please try again.'
        this.loading = false
      })
    },
    waitForContainer() {
      return new Promise((resolve, reject) => {
        let attempts = 0
        const maxAttempts = 20 // 1 second max wait

        const check = () => {
          attempts += 1

          if (this.$refs.mapContainer && this.$refs.mapContainer.offsetWidth > 0) {
            resolve()
          } else if (attempts >= maxAttempts) {
            reject(new Error('Container not found after timeout'))
          } else {
            setTimeout(check, 50)
          }
        }
        check()
      })
    },
    createMap() {
      // At this point, we should have a valid container (checked in ensureMap)
      if (!this.$refs.mapContainer) {
        throw new Error('Map container not found')
      }

      // Determine map center
      let mapCenter = { lat: 6.927079, lng: 79.861244 } // Default center (Sri Lanka)

      if (this.locationData && this.locationData.latitude && this.locationData.longitude) {
        mapCenter = {
          lat: this.locationData.latitude,
          lng: this.locationData.longitude,
        }
      }

      this.map = new window.google.maps.Map(this.$refs.mapContainer, {
        center: mapCenter,
        zoom: this.locationData && this.locationData.latitude && this.locationData.longitude ? 15 : 12,
        streetViewControl: true,
        mapTypeControl: true,
        fullscreenControl: true,
        zoomControl: true,
      })

      this.geocoder = new window.google.maps.Geocoder()

      // Ensure map renders properly
      this.ensureMapRendering()

      // Add marker if location data is available
      if (this.locationData && this.locationData.latitude && this.locationData.longitude) {
        this.addMarker()
      }
    },
    ensureMapRendering() {
      if (!this.map) return

      // Optimized single render trigger
      const triggerRender = () => {
        if (!this.map) return

        // Use a single resize trigger to avoid performance issues
        window.google.maps.event.trigger(this.map, 'resize')

        // Force map to be visible
        this.map.setOptions({
          visible: true,
        })

        // Add event listeners for debugging (only once)
        window.google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
          // Map tiles loaded successfully
        })

        window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
          // Map is idle and ready
        })
      }

      // Use requestAnimationFrame for optimal timing (single call)
      requestAnimationFrame(() => {
        triggerRender()
        // Single delayed trigger as fallback
        setTimeout(triggerRender, 100)
      })
    },
    addMarker() {
      if (!this.map || !this.locationData) {
        return
      }

      // If we have coordinates, use them
      if (this.locationData.latitude && this.locationData.longitude) {
        const position = {
          lat: this.locationData.latitude,
          lng: this.locationData.longitude,
        }

        // Create marker
        this.marker = new window.google.maps.Marker({
          position,
          map: this.map,
          title: this.locationData.address || 'Location',
          animation: window.google.maps.Animation.DROP,
        })

        // Create info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div class="p-2">
              <h6 class="mb-1">${this.locationData.address || 'Location'}</h6>
              <div class="text-muted small">
                ${this.locationData.latitude}, ${this.locationData.longitude}
              </div>
            </div>
          `,
        })

        // Show info window on marker click
        this.marker.addListener('click', () => {
          infoWindow.open(this.map, this.marker)
        })

        // Center map on marker
        this.map.setCenter(position)
      } else if (this.locationData.address) {
        // If we only have address, try to geocode it
        this.geocodeAddress()
      }
    },
    geocodeAddress() {
      if (!this.geocoder || !this.locationData.address) {
        return
      }

      this.geocoder.geocode({ address: this.locationData.address }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const result = results[0]
          const position = result.geometry.location

          // Create marker at geocoded location
          this.marker = new window.google.maps.Marker({
            position,
            map: this.map,
            title: this.locationData.address,
            animation: window.google.maps.Animation.DROP,
          })

          // Create info window
          const infoWindow = new window.google.maps.InfoWindow({
            content: `
              <div class="p-2">
                <h6 class="mb-1">${this.locationData.address}</h6>
                <div class="text-muted small">
                  ${position.lat()}, ${position.lng()}
                </div>
              </div>
            `,
          })

          // Show info window on marker click
          this.marker.addListener('click', () => {
            infoWindow.open(this.map, this.marker)
          })

          // Center map on geocoded location
          this.map.setCenter(position)
        } else {
          // If geocoding fails, show a message
          this.error = 'Unable to locate the address on the map. Please check the address format.'
        }
      })
    },
    openInGoogleMaps() {
      if (!this.locationData) {
        return
      }

      let url = 'https://www.google.com/maps/search/?api=1&query='

      if (this.locationData.latitude && this.locationData.longitude) {
        // Use coordinates if available
        url += `${this.locationData.latitude},${this.locationData.longitude}`
      } else if (this.locationData.address) {
        // Use address if coordinates not available
        url += encodeURIComponent(this.locationData.address)
      } else {
        return
      }

      window.open(url, '_blank')
    },
    cleanupMap() {
      if (this.marker) {
        this.marker.setMap(null)
        this.marker = null
      }
      if (this.map) {
        this.map = null
      }
      if (this.geocoder) {
        this.geocoder = null
      }
      this.retryCount = 0
    },
    retryMapInitialization() {
      this.error = null
      this.retryCount = 0

      // Clean up any failed script loads
      this.cleanupFailedScripts()

      this.initializeMap()
    },
    cleanupFailedScripts() {
      // Remove any existing Google Maps scripts that might be stuck
      const existingScript = document.getElementById('gmaps-view-modal-script')
      if (existingScript) {
        existingScript.remove()
      }

      // Reset Google Maps state
      if (window.google) {
        delete window.google
      }
    },
    forceMapRender() {
      if (this.map && this.$refs.mapContainer) {
        // Force map to resize and redraw
        window.google.maps.event.trigger(this.map, 'resize')
        setTimeout(() => {
          window.google.maps.event.trigger(this.map, 'resize')
        }, 100)
      } else {
        this.retryMapInitialization()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.map-container {
  position: relative;
  background: #f8f9fa;
  border-radius: 6px;
}

.alert {
  margin-bottom: 0;
}

.bg-light {
  background-color: #f8f9fa !important;
}
</style>
